import type { ReactNode } from "react";
import { StarRow } from "../../ui/Icon";
import { Mascot } from "../../ui/Art";
import { t, useLang as useUiLang } from "../../lib/i18n";
import { Link } from "react-router-dom";
import { useMe } from "../../hooks/useMe";

/* ── Language ────────────────────────────────────────────────────────────
 * The UI follows the site language (🌐 in the header). Learning content exists in BM and English:
 * English UI shows English content; every other UI language shows the BM content (KSSR medium). */
export type Lang = "ms" | "en";

export function useLang() {
  const ui = useUiLang();
  const lang: Lang = ui === "en" ? "en" : "ms";
  /** Pick the content text for the current language, falling back to the other one. */
  const pick = (ms: string | null | undefined, en: string | null | undefined) => (lang === "en" ? en || ms : ms || en) ?? "";
  return { lang, pick };
}

/* ── Layout bits ─────────────────────────────────────────────────────────── */
export function TopBar({ back, title, subtitle }: { back?: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      {back && (
        <Link to={back} aria-label={t("Kembali")}
          className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-card text-lg shadow-sm ring-1 ring-black/5">
          ←
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
  );
}

export function Progress({ value, max, tone = "bg-emerald-500" }: { value: number; max: number; tone?: string }) {
  const pct = max > 0 ? Math.round((100 * value) / max) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-black/10" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className={`h-full rounded-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Stars({ n, size = "text-2xl" }: { n: number; size?: string }) {
  return <StarRow n={n} className={size === "text-base" ? "h-5 w-5" : size === "text-4xl" || size === "text-5xl" ? "h-10 w-10" : "h-7 w-7"} />;
}

/** Year card colours (Tahun 1–6, then Tingkatan 1–5). */
export const YEAR_TONES = [
  "from-rose-400 to-pink-500",
  "from-orange-400 to-amber-500",
  "from-yellow-400 to-orange-400",
  "from-emerald-400 to-teal-500",
  "from-sky-400 to-blue-500",
  "from-violet-400 to-purple-500",
];
export const FORM_TONES = ["from-indigo-500 to-blue-600", "from-cyan-500 to-teal-600", "from-fuchsia-500 to-pink-600", "from-lime-500 to-green-600", "from-slate-600 to-slate-800"];
export const toneFor = (level: string, year: number) => (level === "menengah" ? FORM_TONES : YEAR_TONES)[(year - 1) % 6] ?? YEAR_TONES[0]!;

export const SUBJECT_EMOJI: Record<string, string> = { bahasa_melayu: "📖", bahasa_inggeris: "🔤", matematik: "🔢", sains: "🔬", sejarah: "🏛️" };

/** "Tahun 4" / "Year 4" / "4年级"… in the UI language. */
export const levelName = (level: string, year: number) =>
  level === "menengah" ? t("Tingkatan {n}", { n: year }) : t("Tahun {n}", { n: year });

/** Everything in JomBelajar needs a login + licence (admins pass). */
export function StudyGate({ children }: { children: ReactNode }) {
  const { me, loading } = useMe();
  if (loading) return <p className="p-6 text-sm text-slate-500">{t("Memuatkan…")}</p>;
  if (!me)
    return (
      <Card className="text-center">
        <Mascot pose="boy-wave" className="mx-auto h-28 w-28" />
        <p className="mt-2 font-semibold">{t("Log masuk untuk mula belajar.")}</p>
        <Link to="/login?next=/belajar" className="mt-3 inline-block rounded-full bg-emerald-500 px-5 py-2 text-sm font-bold text-white">{t("Log masuk")}</Link>
      </Card>
    );
  if (!me.licenseActive)
    return (
      <Card className="text-center">
        <Mascot pose="girl-think" className="mx-auto h-28 w-28" />
        <p className="mt-2 font-semibold">{t("JomBelajar untuk pemegang lesen JomCerdik.")}</p>
        <Link to="/beli" className="mt-3 inline-block rounded-full bg-emerald-500 px-5 py-2 text-sm font-bold text-white">{t("Dapatkan lesen")}</Link>
      </Card>
    );
  return <>{children}</>;
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-3xl bg-card p-5 shadow-sm ring-1 ring-black/5 ${className}`}>{children}</div>;
}

/** Soft page background used by every JomBelajar screen. */
export function Screen({ children }: { children: ReactNode }) {
  return <div className="min-h-[60vh]">{children}</div>;
}

/** DSKP headings are often ALL CAPS ("MANUSIA"); show them as "Manusia". */
export const titleCase = (s: string) => (s === s.toUpperCase() ? s.toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase()) : s);
